import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { Skeleton } from 'src/common';
import { scrollToItem } from 'src/helpers/scrollToElement';
import styles from './index.module.scss';
import { getNextOffset, isSearchApplied } from 'src/models/procurements/Tenders/helpers';
import { useFetchMore } from 'src/common/infinityScroll';
import { useCurrentFromProc } from 'src/models/procurements/hooks';
import useSelectedTenders from 'src/reactiveVars/SelectedTendersVar';
import { LoadMoreButton } from 'src/shared';
import classNames from 'classnames';
import EmptyMessage from '../../TendersList/EmptyMessage';
import EmptySearchMessage from '../../TendersList/EmptyMessage/EmptySearchMessage';
import DndBoardItem from '../Dnd';
import { ILoadEvent } from 'src/shared/LoadMoreButton';
import { ButtonType } from 'src/common/Button';
import { TendersBoardItem, useTendersBoard } from 'src/models/procurements/TendersViews';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';

interface IProps {
  className?: string;
  loadEvent?: ILoadEvent;
  loadType?: ButtonType;
}
export const TendersBoardList: FC<IProps> = ({ className, loadEvent, loadType }) => {
  const [selectedTenders, setSelectedTenders] = useSelectedTenders();

  const [ref] = useMeasure<HTMLDivElement>();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  const fromProcurementId = useCurrentFromProc();
  const [{ vars: searchArgs, initVars, loading, fetchMore, fetchingMore }] = useApiTenders();

  const { data: tendersData } = useTendersBoard();
  const data = useMemo(() => tendersData?.tenders ?? [], [tendersData]);

  const total = useMemo(() => tendersData?.count ?? 0, [tendersData]);
  const hasMore = data.length < total;

  const loadMore = useCallback(
    (v: { offset: number }) => {
      return fetchMore({
        variables: v
      });
    },
    [fetchMore]
  );

  const offset = useMemo(() => {
    return getNextOffset(data.length, total, initVars.amount ?? 0);
  }, [data.length, initVars.amount, total]);

  const onLoadMore = useFetchMore(offset, loadMore);

  const onSelect = useCallback(
    (selectedTender: TendersBoardItem, isSelected: boolean) => {
      if (isSelected) {
        setSelectedTenders([...selectedTenders, selectedTender]);
      } else {
        const filteredTenders = selectedTenders.filter(tender => tender.id !== selectedTender.id);

        setSelectedTenders(filteredTenders);
      }
    },
    [selectedTenders, setSelectedTenders]
  );

  useEffect(() => {
    if (fromProcurementId) {
      setTimeout(() => scrollToItem(fromProcurementId), 0);
    }
  }, [fromProcurementId]);

  const selectionIsActive = selectedTenders.length > 0;

  const isEmptyResults = useMemo(() => !!tendersData && !tendersData.count, [tendersData]);
  const isEmptyFilter = useMemo(
    () => !isSearchApplied(searchArgs, initVars) && isEmptyResults,
    [searchArgs, initVars, isEmptyResults]
  );
  const isEmptySearch = useMemo(
    () => isSearchApplied(searchArgs, initVars) && isEmptyResults,
    [searchArgs, initVars, isEmptyResults]
  );
  const isEmptyBuyers = useMemo(
    () => searchArgs?.buyerOrgId !== undefined && isEmptyResults,
    [searchArgs, isEmptyResults]
  );

  const emptyScreen = useMemo(
    () =>
      isEmptyBuyers ? (
        <p className={styles.emptyBuyers}>{t('Competitors.competitorsNoData')}</p>
      ) : isEmptyFilter ? (
        <EmptyMessage />
      ) : isEmptySearch ? (
        <EmptySearchMessage />
      ) : null,
    [isEmptyFilter, isEmptySearch, isEmptyBuyers, t]
  );

  return loading ? (
    <Skeleton loading active />
  ) : (
    <>
      {isEmptyResults ? (
        emptyScreen
      ) : (
        <div className={classNames(styles.scrollContainer, className)} ref={setContainer}>
          <div className={styles.content} ref={ref}>
            <div className={styles.tenders}>
              {data.map(tender => (
                <div key={tender.id} className={styles.tender}>
                  <DndBoardItem tender={tender} selectionIsActive={selectionIsActive} onChangeSelection={onSelect} />
                </div>
              ))}
            </div>
            <div className={styles.loadMoreButton}>
              <LoadMoreButton
                onLoadMore={onLoadMore}
                isLoading={fetchingMore}
                hasMore={hasMore}
                scrollContainer={container}
                event={loadEvent}
                type={loadType}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TendersBoardList;
